import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "inventory-view" }
const _hoisted_2 = { class: "inventory-view__left" }
const _hoisted_3 = { class: "left__header" }
const _hoisted_4 = { class: "content__table-header" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "pagination-buttons" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["disabled"]
const _hoisted_11 = { class: "content__update-status" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = ["disabled"]
const _hoisted_14 = ["disabled"]
const _hoisted_15 = ["disabled"]
const _hoisted_16 = { class: "status" }
const _hoisted_17 = { class: "inventory-view__right" }
const _hoisted_18 = { class: "right__cards" }
const _hoisted_19 = { class: "bold semi-large underline" }

import CounterCard from '@/components/CounterCard.vue';
import { useInventoryManager } from '@/services/inventoryManager';
import { useStateStore } from '@/services/state';
import { computed, onMounted, ref } from 'vue';
import BaseModal from '@/components/modals/BaseModal.vue';

// Services
const currentPageTitle = 'Out of Sync Products';
const switchButtonTitle = 'Custom SKU Management';

// Counter Cards

export default /*@__PURE__*/_defineComponent({
  __name: 'InventoryView',
  setup(__props) {

const inv = useInventoryManager();
const stateStore = useStateStore();

const shopifyVariantsCount = computed(() => inv.consolidatedSKUs.value.filter((sku) => sku.shopifyPrice != null).length);
const acumaticaSKUsCount = computed(() => inv.consolidatedSKUs.value.filter((sku) => sku.acumaticaPrice != null).length);
const nonAcumaticaSKUsCount = computed(() => inv.consolidatedSKUs.value.filter((sku) => sku.shopifyOnly === true).length);
const nonShopifySKUsCount = computed(() => inv.consolidatedSKUs.value.filter((sku) => sku.acumaticaOnly === true).length);

const displayCounterCardInfo = ref(false);
const counterCardTitle = ref('');
const counterCardSkus = ref<string[] | null>(null);

function displayNonAcumaticaSKUs() {
	if (isBusy.value) return;
	counterCardSkus.value = inv.consolidatedSKUs.value.filter((item) => item.shopifyOnly === true).map((item) => item.sku);
	counterCardTitle.value = 'Non-Acumatica SKUs';
	displayCounterCardInfo.value = true;
}

function displayNonShopifySKUs() {
	if (isBusy.value) return;
	counterCardSkus.value = inv.consolidatedSKUs.value.filter((item) => item.acumaticaOnly === true).map((item) => item.sku);
	counterCardTitle.value = 'Non-Shopify SKUs';
	displayCounterCardInfo.value = true;
}

function closeCounterCardInfo() {
	displayCounterCardInfo.value = false;
	counterCardTitle.value = '';
	counterCardSkus.value = null;
}

// Table
const syncProduct_columnHeadings = ['Parent SKU', 'SKUs Affected', 'Effect', 'Acumatica', 'Shopify'];

function onRowClick(parentCode: string, e: PointerEvent) {
	if (isBusy.value) return;

	if (e.ctrlKey) {
		if (inv.selectedSKUs.value.has(parentCode)) {
			inv.selectedSKUs.value.delete(parentCode);
		} else {
			inv.selectedSKUs.value.add(parentCode);
		}
	} else {
		inv.selectedSKUs.value = new Set([parentCode]);
	}
}

// Data Loading and Status
const loadingSKUs = ref(false);
const updatingSKUs = ref(false);
const isBusy = computed(() => loadingSKUs.value || updatingSKUs.value);
const updatesNeeded = computed(() => inv.updatesNeededByParentCode.value.size > 0);

const status = computed(() => {
	if (updatingSKUs.value) return `Updating: ${inv.currentlyUpdatingSKU.value}`;
	if (loadingSKUs.value) return 'Loading...';
	if (!updatesNeeded.value) return 'No Updates Needed';
	if (inv.selectedSKUs.value.size === 0) return '';
	return `Products Selected: ${inv.selectedSKUs.value.size}`;
});

async function loadConsolidatedSKUs() {
	loadingSKUs.value = true;
	inv.selectedSKUs.value = new Set();
	inv.consolidatedSKUs.value = [];

	try {
		await inv.fetchConsolidatedSKUs();
	} catch (error) {
		alert('An error occurred while loading SKUs. Please refresh the page to try again.');
	}
	loadingSKUs.value = false;
}

async function triggerUpdate(updateType: 'all' | 'price' | 'quantity') {
	if (isBusy.value) return;

	if (inv.selectedSKUs.value.size === 0) {
		const shouldUpdateAll = confirm('No SKUs are selected. Continue and update all pending SKUs?');
		if (!shouldUpdateAll) {
			return;
		}
	}

	const updateNecessary = inv.isUpdateNecessary.value[updateType];
	if (!updateNecessary) {
		alert('Update was not necessary for the selected update type.');
		return;
	}

	const updateValid = inv.isUpdateValid.value[updateType];
	if (!updateValid) {
		const shouldContinue = confirm('One or more price updates will set price to 0 while Acumatica quantity is greater than 0. Continue with update?');
		if (!shouldContinue) {
			return;
		}
	}

	updatingSKUs.value = true;
	stateStore.navWarningActive = true;
	try {
		await inv.triggerUpdate(updateType);
	} catch (error) {
		alert('An error occurred during the update process. Please try again.');
	}
	stateStore.navWarningActive = false;
	updatingSKUs.value = false;

	await loadConsolidatedSKUs();
}

onMounted(async () => {
	await loadConsolidatedSKUs();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", { class: "header__titles" }, [
          _cache[8] || (_cache[8] = _createElementVNode("h1", { class: "fira aliceblue" }, "Inventory Manager", -1)),
          _createElementVNode("p", { class: "fira aliceblue semi-large" }, _toDisplayString(currentPageTitle))
        ]),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(inv).searchTerm.value) = $event)),
          placeholder: "Search..."
        }, null, 512), [
          [_vModelText, _unref(inv).searchTerm.value]
        ])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("table", null, [
          _cache[9] || (_cache[9] = _createElementVNode("colgroup", null, [
            _createElementVNode("col", { style: {"width":"25%"} }),
            _createElementVNode("col", { style: {"width":"39%"} }),
            _createElementVNode("col", { style: {"width":"10%"} }),
            _createElementVNode("col", { style: {"width":"13%"} }),
            _createElementVNode("col", { style: {"width":"13%"} })
          ], -1)),
          _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(syncProduct_columnHeadings, (syncProduct_columnHeading) => {
                return _createElementVNode("th", { key: syncProduct_columnHeading }, _toDisplayString(syncProduct_columnHeading), 1)
              }), 64))
            ])
          ])
        ])
      ]),
      _createElementVNode("div", {
        class: _normalizeClass(["content__table-container", { disabled: isBusy.value }])
      }, [
        _createElementVNode("table", null, [
          _cache[10] || (_cache[10] = _createElementVNode("colgroup", null, [
            _createElementVNode("col", { style: {"width":"25%"} }),
            _createElementVNode("col", { style: {"width":"39%"} }),
            _createElementVNode("col", { style: {"width":"10%"} }),
            _createElementVNode("col", { style: {"width":"13%"} }),
            _createElementVNode("col", { style: {"width":"13%"} })
          ], -1)),
          _createElementVNode("tbody", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(inv).rowData.value, (updates) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: updates.parentCode,
                class: _normalizeClass({ selected: _unref(inv).selectedSKUs.value.has(updates.parentCode) }),
                onClick: ($event: any) => (onRowClick(updates.parentCode, $event))
              }, [
                _createElementVNode("td", null, _toDisplayString(updates.parentCode), 1),
                _createElementVNode("td", null, _toDisplayString(updates.skus), 1),
                _createElementVNode("td", null, _toDisplayString(updates.effects), 1),
                _createElementVNode("td", null, _toDisplayString(updates.acumaticaValues), 1),
                _createElementVNode("td", null, _toDisplayString(updates.shopifyValues), 1)
              ], 10, _hoisted_5))
            }), 128))
          ])
        ])
      ], 2),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          disabled: _unref(inv).isFirstPage.value,
          onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(inv).firstPage && _unref(inv).firstPage(...args))),
          class: "icon-button"
        }, [...(_cache[11] || (_cache[11] = [
          _createElementVNode("span", { class: "material-symbols-outlined" }, "first_page", -1)
        ]))], 8, _hoisted_7),
        _createElementVNode("button", {
          disabled: _unref(inv).isFirstPage.value,
          onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(inv).prevPage && _unref(inv).prevPage(...args))),
          class: "icon-button"
        }, [...(_cache[12] || (_cache[12] = [
          _createElementVNode("span", { class: "material-symbols-outlined" }, "chevron_left", -1)
        ]))], 8, _hoisted_8),
        _createElementVNode("p", null, _toDisplayString(_unref(inv).currentPage.value) + " / " + _toDisplayString(_unref(inv).totalPages.value), 1),
        _createElementVNode("button", {
          disabled: _unref(inv).isLastPage.value,
          onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(inv).nextPage && _unref(inv).nextPage(...args))),
          class: "icon-button"
        }, [...(_cache[13] || (_cache[13] = [
          _createElementVNode("span", { class: "material-symbols-outlined" }, "chevron_right", -1)
        ]))], 8, _hoisted_9),
        _createElementVNode("button", {
          disabled: _unref(inv).isLastPage.value,
          onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(inv).lastPage && _unref(inv).lastPage(...args))),
          class: "icon-button"
        }, [...(_cache[14] || (_cache[14] = [
          _createElementVNode("span", { class: "material-symbols-outlined" }, "last_page", -1)
        ]))], 8, _hoisted_10)
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("button", {
          disabled: isBusy.value,
          onClick: loadConsolidatedSKUs
        }, "Reset", 8, _hoisted_12),
        _createElementVNode("button", {
          disabled: isBusy.value || !updatesNeeded.value,
          onClick: _cache[5] || (_cache[5] = ($event: any) => (triggerUpdate('all')))
        }, "Update", 8, _hoisted_13),
        _createElementVNode("button", {
          disabled: isBusy.value || !updatesNeeded.value,
          onClick: _cache[6] || (_cache[6] = ($event: any) => (triggerUpdate('price')))
        }, "Update Price Only", 8, _hoisted_14),
        _createElementVNode("button", {
          disabled: isBusy.value || !updatesNeeded.value,
          onClick: _cache[7] || (_cache[7] = ($event: any) => (triggerUpdate('quantity')))
        }, "Update Quantity Only", 8, _hoisted_15),
        _createElementVNode("p", _hoisted_16, _toDisplayString(status.value), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_17, [
      _createElementVNode("div", { class: "right__button" }, [
        _createElementVNode("button", null, _toDisplayString(switchButtonTitle))
      ]),
      _createElementVNode("div", _hoisted_18, [
        _createVNode(CounterCard, {
          title: 'Shopify Variants: ',
          value: loadingSKUs.value ? '-' : shopifyVariantsCount.value
        }, null, 8, ["value"]),
        _createVNode(CounterCard, {
          title: 'Acumatica SKUs: ',
          value: loadingSKUs.value ? '-' : acumaticaSKUsCount.value
        }, null, 8, ["value"]),
        _createVNode(CounterCard, {
          title: 'Non-Acumatica SKUs: ',
          value: loadingSKUs.value ? '-' : nonAcumaticaSKUsCount.value,
          "action-disabled": isBusy.value,
          onExpand: displayNonAcumaticaSKUs,
          icon: 'notes'
        }, null, 8, ["value", "action-disabled"]),
        _createVNode(CounterCard, {
          title: 'Non-Shopify SKUs: ',
          value: loadingSKUs.value ? '-' : nonShopifySKUsCount.value,
          "action-disabled": isBusy.value,
          onExpand: displayNonShopifySKUs,
          icon: 'notes'
        }, null, 8, ["value", "action-disabled"])
      ])
    ]),
    (displayCounterCardInfo.value && counterCardSkus.value)
      ? (_openBlock(), _createBlock(BaseModal, {
          key: 0,
          onClose: closeCounterCardInfo,
          "quick-exit": true
        }, {
          default: _withCtx(() => [
            _createElementVNode("p", _hoisted_19, _toDisplayString(counterCardTitle.value), 1),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(counterCardSkus.value, (sku, index) => {
              return (_openBlock(), _createElementBlock("p", { key: index }, _toDisplayString(index + 1) + ": " + _toDisplayString(sku), 1))
            }), 128))
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})